import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ChevronLeft, ChevronRight, Pin, PinOff, Mic, MicOff, Users, Monitor, LayoutGrid, Rows3 } from "lucide-react";

/**
 * VideoGrid — Zoom-like paginated camera grid with live low-res video.
 *
 * Replaces the old SnapshotGrid (JPEG polling) with actual LiveKit video
 * subscriptions at the LOW simulcast layer (180p / ~100 Kbps per stream).
 * 
 * Features:
 *   - 25 tiles per page (paginated)
 *   - Pin up to 15 participants for higher-quality video
 *   - Three view modes: Speaker, Gallery, Multiple
 *   - Automatic subscription management (only subscribe to visible page)
 *   - IntersectionObserver-driven subscribe/unsubscribe
 *
 * Props:
 *   - participants: array of participant objects from LiveKit room
 *   - pinnedIdentities: currently pinned identity strings
 *   - onTogglePin: (identity) => void
 *   - maxPins: number (default 15)
 *   - activeSpeaker: identity string of current active speaker
 *   - viewMode: "speaker" | "gallery" | "multiple"
 *   - onViewModeChange: (mode) => void
 *   - myIdentity: local participant identity
 *   - subscribeIntent: (identity, source, wantSubscribed) => void
 *   - roomMeta: room metadata for role badges
 *   - currentDjUserId: identity of current DJ
 */

const TILES_PER_PAGE = 25;

function VideoTile({ participant, isPinned, onTogglePin, pinDisabled, subscribeIntent, isActiveSpeaker }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [hasVideo, setHasVideo] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  // Attach video track when available.
  // The participant objects from Room.jsx have a single `videoPublication` field
  // (not `videoTrackPublications`), matching the CamTile pattern.
  useEffect(() => {
    const p = participant;
    if (!p) return;
    const videoEl = videoRef.current;
    const pub = p.videoPublication;
    const videoTrack = pub?.track || null;

    if (videoTrack && videoEl) {
      videoTrack.attach(videoEl);
      setHasVideo(true);
    } else {
      setHasVideo(false);
    }

    // Audio mute: the participant object does not carry audioTrackPublications;
    // infer from the publication existing and not being muted.
    setIsMuted(true); // default muted indicator; parent can override if needed

    return () => {
      if (videoTrack && videoEl) {
        try { videoTrack.detach(videoEl); } catch { /* */ }
      }
    };
  }, [participant, participant?.videoPublication, participant?.videoPublication?.track]);

  // IntersectionObserver for subscription management
  useEffect(() => {
    if (!containerRef.current || !participant?.identity) return;
    const el = containerRef.current;
    const identity = participant.identity;

    const observer = new IntersectionObserver(
      ([entry]) => {
        subscribeIntent(identity, "video", entry.isIntersecting);
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [participant?.identity, subscribeIntent]);

  const displayName = participant?.name || participant?.identity?.slice(0, 8) || "?";
  const initials = displayName.slice(0, 2).toUpperCase();
  const roleTags = [];
  if (participant?.appRole === "host") roleTags.push({ label: "HOST", cls: "bg-amber-400/85 text-black" });
  if (participant?.appRole === "cohost") roleTags.push({ label: "COHOST", cls: "bg-violet-500/85 text-white" });
  if (participant?.isCurrentDj || participant?.role === "dj") roleTags.push({ label: "DJ", cls: "bg-[#FF007F]/85 text-white" });

  return (
    <div
      ref={containerRef}
      className={`relative aspect-video rounded-lg overflow-hidden bg-[#111118] border transition-all ${
        isActiveSpeaker ? "border-[#00F0FF] ring-2 ring-[#00F0FF]/30" : "border-white/[0.06]"
      } ${isPinned ? "ring-2 ring-amber-400/50" : ""} group`}
      data-testid={`video-tile-${participant?.identity}`}
    >
      {/* Video element */}
      <video
        ref={videoRef}
        className={`w-full h-full object-cover ${hasVideo ? "" : "hidden"}`}
        autoPlay
        playsInline
        muted
      />

      {/* Avatar fallback when no video */}
      {!hasVideo && (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1a1a2e] to-[#0f0f1a]">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-lg font-bold text-white/60">
            {initials}
          </div>
        </div>
      )}

      {/* Bottom overlay with name and mic status */}
      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5 flex items-center justify-between">
        <span className="text-[10px] text-white/90 font-medium truncate max-w-[80%]">
          {displayName}
        </span>
        <span className="flex items-center gap-1">
          {isMuted ? (
            <MicOff className="w-3 h-3 text-red-400" />
          ) : (
            <Mic className="w-3 h-3 text-emerald-400" />
          )}
        </span>
      </div>

      {/* Pin button on hover */}
      <button
        onClick={(e) => { e.stopPropagation(); onTogglePin(participant?.identity); }}
        className={`absolute top-1.5 right-1.5 p-1 rounded-md transition-opacity ${
          isPinned ? "opacity-100 bg-amber-500/80" : "opacity-0 group-hover:opacity-100 bg-black/60"
        }`}
        title={isPinned ? "Unpin" : pinDisabled ? "Max pins reached" : "Pin for HD"}
        disabled={!isPinned && pinDisabled}
      >
        {isPinned ? <PinOff className="w-3 h-3 text-white" /> : <Pin className="w-3 h-3 text-amber-300" />}
      </button>

      {/* Live role labels stay separate so a cohost who becomes DJ keeps both tags. */}
      {roleTags.length > 0 && (
        <div className={`absolute left-1.5 flex flex-wrap gap-1 ${isActiveSpeaker ? "top-7" : "top-1.5"}`} data-testid={`video-tile-roles-${participant?.identity}`}>
          {roleTags.map((tag) => <span key={tag.label} className={`px-1.5 py-0.5 rounded text-[8px] font-bold tracking-wide ${tag.cls}`}>{tag.label}</span>)}
        </div>
      )}

      {/* Active speaker indicator */}
      {isActiveSpeaker && (
        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-[#00F0FF]/80 text-[8px] font-bold text-[#0A0A0F] uppercase">
          Speaking
        </div>
      )}
    </div>
  );
}

export default function VideoGrid({
  participants = [],
  pinnedIdentities = [],
  onTogglePin,
  maxPins = 15,
  activeSpeaker,
  viewMode = "gallery",
  onViewModeChange,
  myIdentity,
  subscribeIntent,
  roomMeta,
  currentDjUserId,
}) {
  const [currentPage, setCurrentPage] = useState(0);
  const pinnedSet = useMemo(() => new Set(pinnedIdentities), [pinnedIdentities]);

  // Separate pinned and unpinned participants
  const pinnedParticipants = useMemo(
    () => participants.filter((p) => pinnedSet.has(p.identity)),
    [participants, pinnedSet]
  );
  const unpinnedParticipants = useMemo(
    () => participants.filter((p) => !pinnedSet.has(p.identity)),
    [participants, pinnedSet]
  );

  // Pagination for unpinned participants
  const totalPages = Math.max(1, Math.ceil(unpinnedParticipants.length / TILES_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages - 1);
  const pageParticipants = unpinnedParticipants.slice(
    safePage * TILES_PER_PAGE,
    safePage * TILES_PER_PAGE + TILES_PER_PAGE
  );

  // Reset page when participants change significantly
  useEffect(() => {
    if (currentPage >= totalPages) setCurrentPage(Math.max(0, totalPages - 1));
  }, [totalPages, currentPage]);

  // Unsubscribe from participants not on current page
  useEffect(() => {
    const visibleIdentities = new Set([
      ...pinnedIdentities,
      ...pageParticipants.map((p) => p.identity),
    ]);
    // Unsubscribe from participants that are off-page and not pinned
    unpinnedParticipants.forEach((p) => {
      if (!visibleIdentities.has(p.identity)) {
        subscribeIntent(p.identity, "video", false);
      }
    });
  }, [safePage, unpinnedParticipants, pinnedIdentities, pageParticipants, subscribeIntent]);

  const pinDisabled = pinnedIdentities.length >= maxPins;

  // Speaker view: active speaker gets large tile, rest in strip
  if (viewMode === "speaker") {
    const speaker = activeSpeaker
      ? participants.find((p) => p.identity === activeSpeaker)
      : pinnedParticipants[0] || participants[0];
    // Pinned participants (excluding the speaker) always show in the strip
    const pinnedInStrip = pinnedParticipants.filter((p) => p.identity !== speaker?.identity);
    const unpinnedOthers = unpinnedParticipants.filter((p) => p.identity !== speaker?.identity);
    const unpinnedOthersPage = unpinnedOthers.slice(safePage * TILES_PER_PAGE, safePage * TILES_PER_PAGE + TILES_PER_PAGE);
    const othersTotalPages = Math.max(1, Math.ceil(unpinnedOthers.length / TILES_PER_PAGE));

    return (
      <section data-testid="video-grid" className="flex flex-col gap-3">
        <ViewModeBar viewMode={viewMode} onViewModeChange={onViewModeChange} participantCount={participants.length} />
        
        {/* Large active speaker tile */}
        {speaker && (
          <div className="w-full max-h-[60vh]">
            <div className="aspect-video max-h-[55vh] mx-auto">
              <VideoTile
                participant={speaker}
                isPinned={pinnedSet.has(speaker.identity)}
                onTogglePin={onTogglePin}
                pinDisabled={pinDisabled}
                subscribeIntent={subscribeIntent}
                isActiveSpeaker={true}
              />
            </div>
          </div>
        )}

        {/* Pinned strip (always visible across pages) */}
        {pinnedInStrip.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300/85 font-bold flex items-center gap-1.5 mb-1">
              <Pin className="w-3 h-3" /> Pinned
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {pinnedInStrip.map((p) => (
                <div key={p.identity} className="w-32 flex-shrink-0">
                  <VideoTile
                    participant={p}
                    isPinned={true}
                    onTogglePin={onTogglePin}
                    pinDisabled={false}
                    subscribeIntent={subscribeIntent}
                    isActiveSpeaker={p.identity === activeSpeaker}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Unpinned strip (paginated) */}
        {unpinnedOthers.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {unpinnedOthersPage.slice(0, 8).map((p) => (
              <div key={p.identity} className="w-32 flex-shrink-0">
                <VideoTile
                  participant={p}
                  isPinned={false}
                  onTogglePin={onTogglePin}
                  pinDisabled={pinDisabled}
                  subscribeIntent={subscribeIntent}
                  isActiveSpeaker={p.identity === activeSpeaker}
                />
              </div>
            ))}
            {unpinnedOthers.length > 8 && (
              <div className="w-32 flex-shrink-0 aspect-video rounded-lg bg-white/[0.04] grid place-items-center text-[11px] text-white/45">
                +{unpinnedOthers.length - 8} more
              </div>
            )}
          </div>
        )}

        {othersTotalPages > 1 && (
          <Pagination currentPage={safePage} totalPages={othersTotalPages} onPageChange={setCurrentPage} />
        )}
      </section>
    );
  }

  // Multiple view: pinned large on top, unpinned grid below
  if (viewMode === "multiple") {
    return (
      <section data-testid="video-grid" className="flex flex-col gap-3">
        <ViewModeBar viewMode={viewMode} onViewModeChange={onViewModeChange} participantCount={participants.length} />

        {/* Pinned participants (large tiles) */}
        {pinnedParticipants.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300/85 font-bold flex items-center gap-1.5 mb-2">
              <Pin className="w-3 h-3" /> Pinned · {pinnedParticipants.length}/{maxPins}
            </div>
            <div className={`grid gap-3 ${
              pinnedParticipants.length === 1 ? "grid-cols-1" :
              pinnedParticipants.length <= 4 ? "grid-cols-2" :
              pinnedParticipants.length <= 9 ? "grid-cols-3" :
              "grid-cols-4"
            }`}>
              {pinnedParticipants.map((p) => (
                <VideoTile
                  key={p.identity}
                  participant={p}
                  isPinned={true}
                  onTogglePin={onTogglePin}
                  pinDisabled={false}
                  subscribeIntent={subscribeIntent}
                  isActiveSpeaker={p.identity === activeSpeaker}
                />
              ))}
            </div>
          </div>
        )}

        {/* Unpinned participants (smaller grid) */}
        {unpinnedParticipants.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">
              Others · {unpinnedParticipants.length} participant{unpinnedParticipants.length === 1 ? "" : "s"}
            </div>
            <div className="grid gap-2 grid-cols-3 sm:grid-cols-4 md:grid-cols-5">
              {pageParticipants.map((p) => (
                <VideoTile
                  key={p.identity}
                  participant={p}
                  isPinned={false}
                  onTogglePin={onTogglePin}
                  pinDisabled={pinDisabled}
                  subscribeIntent={subscribeIntent}
                  isActiveSpeaker={p.identity === activeSpeaker}
                />
              ))}
            </div>
            {totalPages > 1 && (
              <Pagination currentPage={safePage} totalPages={totalPages} onPageChange={setCurrentPage} />
            )}
          </div>
        )}
      </section>
    );
  }

  // Gallery view (default): pinned always visible at top, unpinned paginated below
  return (
    <section data-testid="video-grid" className="flex flex-col gap-3">
      <ViewModeBar viewMode={viewMode} onViewModeChange={onViewModeChange} participantCount={participants.length} />

      {/* Pinned participants always visible regardless of page */}
      {pinnedParticipants.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300/85 font-bold flex items-center gap-1.5 mb-2">
            <Pin className="w-3 h-3" /> Pinned · {pinnedParticipants.length}/{maxPins}
          </div>
          <div className={`grid gap-2 ${
            pinnedParticipants.length <= 1 ? "grid-cols-1" :
            pinnedParticipants.length <= 4 ? "grid-cols-2" :
            pinnedParticipants.length <= 9 ? "grid-cols-3" :
            pinnedParticipants.length <= 16 ? "grid-cols-4" :
            "grid-cols-5"
          }`}>
            {pinnedParticipants.map((p) => (
              <VideoTile
                key={p.identity}
                participant={p}
                isPinned={true}
                onTogglePin={onTogglePin}
                pinDisabled={false}
                subscribeIntent={subscribeIntent}
                isActiveSpeaker={p.identity === activeSpeaker}
              />
            ))}
          </div>
        </div>
      )}

      {/* Unpinned participants in paginated grid */}
      {unpinnedParticipants.length > 0 && (
        <div>
          {pinnedParticipants.length > 0 && (
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2 mt-2">
              Others · {unpinnedParticipants.length}
            </div>
          )}
          <div className={`grid gap-2 ${
            pageParticipants.length <= 1 ? "grid-cols-1" :
            pageParticipants.length <= 4 ? "grid-cols-2" :
            pageParticipants.length <= 9 ? "grid-cols-3" :
            pageParticipants.length <= 16 ? "grid-cols-4" :
            "grid-cols-5"
          }`}>
            {pageParticipants.map((p) => (
              <VideoTile
                key={p.identity}
                participant={p}
                isPinned={false}
                onTogglePin={onTogglePin}
                pinDisabled={pinDisabled}
                subscribeIntent={subscribeIntent}
                isActiveSpeaker={p.identity === activeSpeaker}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <Pagination currentPage={safePage} totalPages={totalPages} onPageChange={setCurrentPage} />
          )}
        </div>
      )}
    </section>
  );
}

function ViewModeBar({ viewMode, onViewModeChange, participantCount }) {
  const modes = [
    { key: "speaker", label: "Speaker", Icon: Monitor },
    { key: "gallery", label: "Gallery", Icon: LayoutGrid },
    { key: "multiple", label: "Multiple", Icon: Rows3 },
  ];

  return (
    <div className="flex items-center justify-between mb-1">
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 flex items-center gap-1.5">
        <Users className="w-3 h-3" />
        {participantCount} participant{participantCount === 1 ? "" : "s"}
      </div>
      <div className="flex items-center gap-1 bg-white/[0.04] rounded-lg p-0.5 border border-white/[0.06]">
        {modes.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => onViewModeChange(key)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] uppercase tracking-[0.12em] font-bold transition-colors ${
              viewMode === key
                ? "bg-gradient-to-r from-[#FF007F]/80 to-[#00F0FF]/80 text-white"
                : "text-white/50 hover:text-white/80 hover:bg-white/5"
            }`}
            title={label}
            data-testid={`view-mode-${key}`}
          >
            <Icon className="w-3 h-3" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
  return (
    <div className="flex items-center justify-center gap-3 mt-3" data-testid="video-grid-pagination">
      <button
        onClick={() => onPageChange(Math.max(0, currentPage - 1))}
        disabled={currentPage === 0}
        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
        aria-label="Previous page"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <div className="text-xs font-mono text-white/60">
        {currentPage + 1} / {totalPages}
      </div>
      <button
        onClick={() => onPageChange(Math.min(totalPages - 1, currentPage + 1))}
        disabled={currentPage >= totalPages - 1}
        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
        aria-label="Next page"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}
